import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';
import HeroCarousel from './components/HeroCarousel';
import SectionHeading from '@/components/base/SectionHeading';
import MovieCard from '@/components/base/MovieCard';

const heroIds = ['1', '7', '12', '3'];
const heroMovies = heroIds
  .map((id) => movies.find((m) => m.id === id))
  .filter((m): m is (typeof movies)[number] => Boolean(m));

const categories = [
  { name: '剧情', icon: 'ri-film-line' },
  { name: '爱情', icon: 'ri-heart-line' },
  { name: '悬疑', icon: 'ri-search-eye-line' },
  { name: '犯罪', icon: 'ri-alarm-warning-line' },
  { name: '文艺', icon: 'ri-book-open-line' },
  { name: '家庭', icon: 'ri-home-heart-line' },
  { name: '科幻', icon: 'ri-planet-line' },
  { name: '喜剧', icon: 'ri-emotion-happy-line' },
  { name: '都市', icon: 'ri-building-line' },
  { name: '青春', icon: 'ri-plant-line' },
];

const latestNews = [...news].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 6);
const hotMovies = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 8);

export default function Home() {
  return (
    <div className="w-full">
      <HeroCarousel movies={heroMovies} />

      {/* 分类快捷入口 */}
      <section className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
        <SectionHeading
          title="按类型浏览"
          subtitle="快速进入你感兴趣的电影分类"
          action={
            <Link
              to="/movies"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 whitespace-nowrap"
            >
              全部电影
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          }
        />
        <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-10 gap-3">
          {categories.map((c) => (
            <Link
              key={c.name}
              to={`/movies?genre=${encodeURIComponent(c.name)}`}
              className="group flex flex-col items-center gap-2 py-4 rounded-lg bg-background-100 border border-background-200 hover:bg-primary-50 hover:border-primary-200 transition-colors"
            >
              <span className="w-11 h-11 rounded-full bg-background-50 flex items-center justify-center text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={`${c.icon} text-xl`}></i>
              </span>
              <span className="text-xs md:text-sm text-foreground-700 group-hover:text-primary-600 font-medium">
                {c.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 专题横幅 */}
      <section className="max-w-content mx-auto px-4 md:px-6">
        <div className="relative rounded-lg overflow-hidden bg-primary-900">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20still%20of%20an%20empty%20long%20dinner%20table%20under%20warm%20hanging%20lamps%20with%20deep%20shadows%2C%20moody%20atmospheric%20lighting%2C%20deep%20navy%20and%20amber%20tones%2C%20film%20grain%2C%20high%20detail&width=1600&height=520&seq=home-feature-band&orientation=landscape"
            alt="聚会的目的电影专题"
            title="聚会的目的电影 - 剧情解读专题"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
          />
          <div className="relative px-6 md:px-12 py-10 md:py-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold">
              <i className="ri-bookmark-line"></i>
              本站专题
            </span>
            <h2 className="mt-4 text-2xl md:text-3xl font-bold text-white">《聚会的目的》深度解析专题</h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-white/80 leading-relaxed">
              从剧情线索到人物关系，从镜头语言到隐藏细节，我们把关于这部电影的解读、影评与幕后资讯整理成一处，
              帮你在观看之前之后都更懂它。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/movies/detail/1"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                阅读剧情与影评
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-white/40 text-white text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                浏览相关资讯
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 最新资讯 + 热门电影 */}
      <section className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <SectionHeading
              title="最新影视资讯"
              subtitle="电影动态、深度解读与行业观察"
              action={
                <Link
                  to="/news"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 whitespace-nowrap"
                >
                  更多资讯
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              }
            />
            <ul className="mt-6 divide-y divide-background-200">
              {latestNews.map((n) => (
                <li key={n.id}>
                  <Link to={`/news/detail/${n.id}`} className="group flex gap-4 py-4">
                    <span className="w-24 h-16 md:w-32 md:h-20 rounded-md overflow-hidden bg-foreground-100 shrink-0">
                      <img
                        src={n.cover}
                        alt={n.title}
                        title={`${n.title} - 影视资讯`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 text-xs text-foreground-400">
                        <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700">
                          {n.category}
                        </span>
                        <span className="tabular-nums">{n.date}</span>
                      </span>
                      <h3 className="mt-1.5 text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {n.title}
                      </h3>
                      <p className="mt-1 text-xs md:text-sm text-foreground-500 line-clamp-2 hidden sm:block">
                        {n.excerpt}
                      </p>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <SectionHeading title="热门电影推荐" subtitle="观众评分最高的作品" />
            <div className="mt-6 grid grid-cols-2 gap-3 md:gap-4">
              {hotMovies.slice(0, 4).map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 高分电影 */}
      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
          <SectionHeading title="高分口碑片单" subtitle="经得起回味的电影，值得一看再看" />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {hotMovies.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="max-w-content mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            {
              icon: 'ri-file-text-line',
              title: '详尽剧情介绍',
              desc: '不剧透关键，也能帮你理清人物关系与叙事线索，快速了解一部电影讲了什么。',
            },
            {
              icon: 'ri-chat-quote-line',
              title: '专业影评观点',
              desc: '汇集影评人与观众的多角度观点，既有深度分析，也有真诚的观影感受。',
            },
            {
              icon: 'ri-newspaper-line',
              title: '及时影视资讯',
              desc: '聚焦新片定档、主创访谈与行业观察，让你不错过值得关注的电影动态。',
            },
          ].map((f) => (
            <div
              key={f.title}
              className="p-6 rounded-lg bg-background-50 border border-background-200"
            >
              <span className="w-12 h-12 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
                <i className={`${f.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">{f.title}</h3>
              <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}